import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';


/** Supported theme modes. */
export type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  /** The user-selected mode (persisted to localStorage). */
  mode: ThemeMode;
  /** The resolved appearance (same as mode since no system option). */
  resolvedTheme: 'light' | 'dark';
  /** Change the theme mode. */
  setMode: (mode: ThemeMode) => void;
}

const STORAGE_KEY = 'tf-theme';

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Read the persisted theme mode from localStorage.
 * Falls back to `'dark'` when nothing is stored.
 */
function getStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'dark';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }
  return 'dark';
}

/**
 * Resolve the effective appearance given a mode.
 */
function resolve(mode: ThemeMode): 'light' | 'dark' {
  return mode;
}

/**
 * Apply or remove the `.dark` class on `<html>` and update `color-scheme`.
 */
function applyTheme(resolved: 'light' | 'dark') {
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  root.style.colorScheme = resolved;
}

interface ThemeProviderProps {
  children: ReactNode;
  /** Override the default mode (useful for testing / SSR). */
  defaultMode?: ThemeMode;
}

/**
 * Provides light/dark theme state to the component tree.
 *
 * - Persists the selected mode to `localStorage('tf-theme')`.
 * - Applies `.dark` class on `<html>` so Tailwind `dark:` variants activate.
 */
export function ThemeProvider({ children, defaultMode }: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(
    () => defaultMode ?? getStoredMode(),
  );

  const resolvedTheme = resolve(mode);

  // Apply .dark class whenever resolvedTheme changes.
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolvedTheme, setMode }),
    [mode, resolvedTheme, setMode],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

/**
 * Access the current theme context.
 *
 * @throws if used outside `<ThemeProvider>`.
 *
 * @example
 * ```tsx
 * const { mode, resolvedTheme, setMode } = useTheme();
 * ```
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a <ThemeProvider>');
  }
  return ctx;
}
